import { FormikErrors, FormikTouched } from 'formik';
import { t } from 'i18next';
import { IconButton, QuerySelect, Textarea } from '~/ui';
import styles from './LeisureGeneral.module.scss';
import ImageSelect from '~/ui/ImageSelect/ImageSelect';
import CloseIcon from '~/assets/svg/newSvg/close-cross.svg?react';
import { useContext, useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { AccommodationItem } from '../AccommodationForm';
import { Modal } from '../Shared/Modal/Modal';
import PhotoPickerContainer from '../AnnouncementForm/PhotoPickerContainer/PhotoPickerContainer';
import { IFormikValues } from '~/pages/Leisure/Leisure';
import classNames from 'classnames';
import BtnIcon from '~/assets/svg/newSvg/picture.svg?react';
import { AuthenticationContext } from '../Authentication/AuthenticationProvider';
import { checkIfUserSuperAdmin } from '~/utils/getUserRole';
import cn from 'classnames';
import {
  getAccommodationById,
  getAccommodationsInfinite,
  getAssetById,
  getAssetsInfinite,
  getOrganizationsInfinite,
} from '~/utils/api/queries';

interface ILeisureGeneralProps {
  values: IFormikValues;
  setFieldValue: (name: string, value: any) => void;
  setFieldTouched: (name: string, value: boolean) => void;
  resetImage: () => void;
  errors: FormikErrors<IFormikValues>;
  touched: FormikTouched<IFormikValues>;
}

const LeisureGeneral = ({
  values,
  setFieldValue,
  resetImage,
  errors,
  touched,
}: ILeisureGeneralProps) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { user } = useContext(AuthenticationContext);
  const queryClient = useQueryClient();
  const isSuperAdmin = checkIfUserSuperAdmin(user?.data.role_name);

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setFieldValue('uploadImage', file);
    }
  };

  useEffect(() => {
    if (!values.accommodations.length || !values.assets.length) {
      setFieldValue('organizations', []);
    }
  }, [values.accommodations, values.assets]);

  return (
    <div className={styles.container}>
      <div className={styles.selectWrapper}>
        {isSuperAdmin && (
          <QuerySelect
            label={t('asset')}
            placeholder={t('assets')}
            value={values.assets}
            queryFn={({ pageParam, meta }) =>
              getAssetsInfinite({
                page: pageParam,
                perPage: 15,
                query: meta?.searchQuery as string,
              })
            }
            queryKey={['assets-leisure']}
            getItems={getAssetById}
            multiple
            star={true}
            emptyText={t('nothing_found')}
            onChange={(value: number[]) => {
              setFieldValue('assets', value);
              const filteredAccommodations = values.accommodations.filter(accId => {
                const cached = queryClient.getQueryData<AccommodationItem>(
                  [['accommodations-leisure'], 'item', accId]
                );
                return cached ? value.includes(cached.asset_id!) : true;
              });
              setFieldValue('accommodations', filteredAccommodations);
              if (filteredAccommodations.length === 0) {
                setFieldValue('organizations', []);
              } else {
                getOrganizationsInfinite({ accommodation_ids: filteredAccommodations }).then(data => {
                  const validOrgIds = new Set(data.data.map(o => o.id));
                  setFieldValue('organizations', values.organizations.filter(id => validOrgIds.has(id)));
                });
              }
            }}
            error={errors.assets as string}
            className={cn(styles.select, styles.superAdminSelect)}
          />
        )}
        <QuerySelect
          label={t('accommodations')}
          placeholder={t('select_accommodation')}
          value={values.assets.length === 0 ? [] : values.accommodations}
          queryFn={({ pageParam, meta }) =>
            getAccommodationsInfinite({
              sort: '',
              field: '',
              asset_ids: values.assets,
              page: pageParam,
              perPage: 15,
              query: meta?.searchQuery as string,
            })
          }
          queryKey={['accommodations-leisure']}
          updateOn={values.assets.join(',')}
          getItems={getAccommodationById}
          multiple
          star={true}
          noCode
          emptyText={t('nothing_found')}
          disabled={!values.assets.length}
          onChange={(value: number[]) => {
            setFieldValue('accommodations', value);
            if (value.length === 0) {
              setFieldValue('organizations', []);
              return;
            }
            getOrganizationsInfinite({ accommodation_ids: value }).then(data => {
              const validOrgIds = new Set(data.data.map(o => o.id));
              setFieldValue(
                'organizations',
                values.organizations.filter(id => validOrgIds.has(id))
              );
            });
          }}
          error={errors.accommodations as string}
          className={cn(styles.select, isSuperAdmin && styles.superAdminSelect)}
        />
        <QuerySelect
          label={t('organizations')}
          placeholder={t('select_least_one_organization')}
          value={values.organizations}
          queryFn={({ pageParam, meta }) =>
            getOrganizationsInfinite({
              accommodation_ids: values.accommodations,
              page: pageParam,
              perPage: 15,
              query: meta?.searchQuery as string,
            })
          }
          queryKey={['organizations-leisure']}
          updateOn={values.accommodations.join(',')}
          multiple
          star={true}
          emptyText={t('nothing_found')}
          disabled={!values.accommodations.length || !values.assets.length}
          onChange={(value: number[]) => setFieldValue('organizations', value)}
          error={errors.organizations as string}
          className={cn(styles.select, isSuperAdmin && styles.superAdminSelect)}
        />
      </div>
      <div>
        <Textarea
          label={t('description')}
          rows={4}
          star
          name="description"
          placeholder={t('description_game')}
          value={values.description}
          onChange={e => setFieldValue('description', e.target.value)}
          maxLength={1000}
          errors={errors.description}
          error={!touched.description && Boolean(errors.description)}
        />
      </div>
      {!true && (
        <div className={styles.imageBtnWrapper}>
          <ImageSelect
            image={values.image}
            setFieldValue={setFieldValue}
            setIsImageModalOpen={setIsModalOpen}
          />
          {errors.image && (
            <span className={styles.imageError}>
              {t('choose_photo_for_entertainment')}
            </span>
          )}
        </div>
      )}

      {values.uploadImage && (
        <div className={styles.uploadImageWrapper}>
          <img
            src={
              typeof values.uploadImage === 'string'
                ? values.uploadImage
                : URL.createObjectURL(values.uploadImage)
            }
            alt="Uploaded preview"
            className={styles.uploadImage}
          />
          <button
            className={styles.deleteImageBtn}
            type="button"
            onClick={() => setFieldValue('uploadImage', null)}
          >
            <CloseIcon className={styles.deleteImageIcon} />
          </button>
        </div>
      )}

      {!values.uploadImage && (
        <div className={styles.addImageBtnWrapper}>
          <input
            id="uploadImage"
            type="file"
            accept="image/*"
            onChange={handleImageChange}
            className={styles.addImageBtnInput}
          />
          <IconButton
            onClick={() => document.getElementById('uploadImage')?.click()}
            className={classNames(styles.addImageBtn, styles.addImageBtnIcon)}
          >
            <BtnIcon /> {t('download_game_cover')}
          </IconButton>

          {errors.uploadImage && (
            <span className={styles.imageError}>
              {t('game_cover_validate')}
            </span>
          )}
        </div>
      )}

      <Modal
        modalStyles={styles.photosModal}
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
      >
        <PhotoPickerContainer
          initialPhotos={[]}
          selectedImages={values.image}
          onImageChange={value => setFieldValue('image', value)}
          onResetClick={resetImage}
          onCloseClick={() => setIsModalOpen(false)}
        />
      </Modal>
    </div>
  );
};

export default LeisureGeneral;
